"use client";

import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import {
  replaceClientSearchParams,
  useClientSearchParams,
} from "./useClientSearchParams";

export const useUrlFilters = (
  filtersToReset: string[],
  filtersToResetOnApply: { key: string; value: string | number }[],
) => {
  const pathName = usePathname();
  const params = useClientSearchParams();

  const [pendingFilters, setPendingFilters] = useState<Record<string, string>>(
    {},
  );
  const [isSubmittingFilter, setIsSubmittingFilter] = useState(false);

  useEffect(() => {
    if (!isSubmittingFilter) return;

    // Text search waits for typing to pause; other controls use a short delay
    // to coalesce rapid changes into a single URL update.
    const delay = Object.hasOwn(pendingFilters, "query") ? 450 : 150;
    const timer = window.setTimeout(() => {
      const searchParams = new URLSearchParams(params.toString());

      for (const { key, value } of filtersToResetOnApply) {
        searchParams.set(key, String(value));
      }

      Object.entries(pendingFilters).forEach(([key, value]) => {
        value ? searchParams.set(key, value) : searchParams.delete(key);
      });

      replaceClientSearchParams(pathName, searchParams);
      setPendingFilters({});
      setIsSubmittingFilter(false);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [
    filtersToResetOnApply,
    isSubmittingFilter,
    params,
    pathName,
    pendingFilters,
  ]);

  const resetFilters = useCallback(() => {
    const searchParams = new URLSearchParams(params.toString());
    for (const filter of filtersToReset) {
      searchParams.delete(filter);
    }
    replaceClientSearchParams(pathName, searchParams);
    setPendingFilters({});
    setIsSubmittingFilter(false);
  }, [pathName, params, filtersToReset]);

  const changeFilter = useCallback(
    (key: string) => (value: string) => () => {
      setPendingFilters((prev) => {
        const newFilters = { ...prev, [key]: value };
        const hasChanges = Object.entries(newFilters).some(
          ([k, v]) => params.get(k) !== v || (!v && params.has(k)),
        );
        setIsSubmittingFilter(hasChanges);
        return newFilters;
      });
    },
    [params],
  );

  const hasActiveFilters = useCallback(() => {
    const urlActive = filtersToReset.some((key) => params.has(key));
    const pendingActive = Object.keys(pendingFilters).length > 0;
    return urlActive || pendingActive;
  }, [params, filtersToReset, pendingFilters]);

  const getCurrentFilterState = useCallback(
    (key: string) => pendingFilters[key] ?? params.get(key) ?? "",
    [pendingFilters, params],
  );

  return {
    isSubmittingFilter,
    resetFilters,
    changeFilter,
    hasActiveFilters,
    getCurrentFilterState,
  };
};
